import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  FileText,
  AlertTriangle,
  Edit,
  Eye,
  Download,
  Share,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useAccessControl } from '../hooks/useAccessControl';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DurationSelector from './DurationSelector';

interface AccessRequest {
  id: string;
  patient_id: string;
  requester_id: string;
  permission_type: string;
  data_type: string;
  justification: string;
  requested_duration?: string;
  status: string;
  created_at: string;
  requester?: {
    id: string;
    full_name: string;
    email: string;
    role: string;
  };
}

interface DoctorGroupedApprovalMatrixProps {
  requests: AccessRequest[];
  onRequestProcessed?: () => void;
  className?: string;
}

const DoctorGroupedApprovalMatrix: React.FC<DoctorGroupedApprovalMatrixProps> = ({
  requests,
  onRequestProcessed,
  className
}) => {
  const { reviewAccessRequest, loading } = useAccessControl();
  const [expandedDoctors, setExpandedDoctors] = useState<Set<string>>(new Set());
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, Record<string, boolean>>>({});
  
  // Inicializar seleções com base nas solicitações do médico
  React.useEffect(() => {
    const initialSelections: Record<string, Record<string, boolean>> = {};
    
    Object.entries(groupedRequests).forEach(([doctorId, { requests: doctorRequests }]) => {
      initialSelections[doctorId] = {};
      doctorRequests.forEach(request => {
        // Pré-marcar todas as solicitações do médico
        initialSelections[doctorId][request.id] = true;
      });
    });
    
    setSelectedPermissions(initialSelections);
  }, [requests]);
  const [customDuration, setCustomDuration] = useState('30 days');
  const [reviewNotes, setReviewNotes] = useState('');
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());

  // Tipos de permissão e dados para a matriz (idênticos ao PermissionGridSelector)
  const permissionTypes = [
    {
      type: 'read',
      label: 'Visualizar',
      icon: <Eye className="h-4 w-4" />,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100'
    },
    {
      type: 'write',
      label: 'Editar',
      icon: <Edit className="h-4 w-4" />,
      color: 'bg-green-500',
      lightColor: 'bg-green-100'
    },
    {
      type: 'download',
      label: 'Baixar',
      icon: <Download className="h-4 w-4" />,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100'
    },
    {
      type: 'share',
      label: 'Compartilhar',
      icon: <Share className="h-4 w-4" />,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100'
    },
    {
      type: 'delete',
      label: 'Excluir',
      icon: <Trash2 className="h-4 w-4" />,
      color: 'bg-red-500',
      lightColor: 'bg-red-100'
    }
  ];

  const dataTypes = [
    {
      type: 'medical_records',
      label: 'Registros Médicos',
      shortLabel: 'Registros',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-blue-700'
    },
    {
      type: 'documents',
      label: 'Documentos',
      shortLabel: 'Docs',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-green-700'
    },
    {
      type: 'lab_results',
      label: 'Resultados de Exames',
      shortLabel: 'Exames',
      icon: <User className="h-4 w-4" />,
      color: 'text-purple-700'
    },
    {
      type: 'prescriptions',
      label: 'Prescrições',
      shortLabel: 'Receitas',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-orange-700'
    },
    {
      type: 'imaging',
      label: 'Exames de Imagem',
      shortLabel: 'Imagens',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-pink-700'
    },
    {
      type: 'personal_info',
      label: 'Informações Pessoais',
      shortLabel: 'Pessoais',
      icon: <User className="h-4 w-4" />,
      color: 'text-yellow-700'
    }
  ];

  // Agrupar solicitações por médico
  const groupedRequests = requests.reduce((groups, request) => {
    const doctorId = request.requester_id;
    const doctorName = request.requester?.full_name || 'Médico Desconhecido';
    const doctorEmail = request.requester?.email || '';
    
    if (!groups[doctorId]) {
      groups[doctorId] = {
        doctor: { id: doctorId, name: doctorName, email: doctorEmail },
        requests: []
      };
    }
    
    groups[doctorId].requests.push(request);
    return groups;
  }, {} as Record<string, { doctor: { id: string; name: string; email: string }; requests: AccessRequest[] }>);

  const toggleDoctorExpansion = (doctorId: string) => {
    const newExpanded = new Set(expandedDoctors);
    if (newExpanded.has(doctorId)) {
      newExpanded.delete(doctorId);
    } else {
      newExpanded.add(doctorId);
    }
    setExpandedDoctors(newExpanded);
  };

  const togglePermissionSelection = (doctorId: string, requestId: string) => {
    setSelectedPermissions(prev => {
      const currentValue = prev[doctorId]?.[requestId];
      // Permite alternar entre true e false livremente
      return {
        ...prev,
        [doctorId]: {
          ...prev[doctorId],
          [requestId]: !currentValue
        }
      };
    });
  };

  const selectAllForDoctor = (doctorId: string, requests: AccessRequest[]) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [doctorId]: requests.reduce((acc, req) => ({ ...acc, [req.id]: true }), {})
    }));
  };

  const deselectAllForDoctor = (doctorId: string) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [doctorId]: {}
    }));
  };

  const handleProcessAllRequests = async (doctorId: string, requests: AccessRequest[]) => {
    const allRequestIds = requests.map(req => req.id);
    const selectedRequestIds = Object.keys(selectedPermissions[doctorId] || {})
      .filter(requestId => selectedPermissions[doctorId][requestId]);
    const deniedRequestIds = allRequestIds.filter(id => !selectedRequestIds.includes(id));
    
    if (allRequestIds.length === 0) {
      toast.error('Nenhuma solicitação para processar');
      return;
    }

    setProcessingRequests(new Set(allRequestIds));
    
    try {
      const results = [];
      
      // Aprovar solicitações selecionadas
      if (selectedRequestIds.length > 0) {
        const approvalResults = await Promise.all(
          selectedRequestIds.map(requestId => 
            reviewAccessRequest(requestId, 'approved', reviewNotes, customDuration)
          )
        );
        results.push(...approvalResults.map(r => ({ ...r, action: 'approved' })));
      }
      
      // Negar solicitações não selecionadas
      if (deniedRequestIds.length > 0) {
        const denialResults = await Promise.all(
          deniedRequestIds.map(requestId => 
            reviewAccessRequest(requestId, 'denied', reviewNotes || 'Não aprovado pelo paciente')
          )
        );
        results.push(...denialResults.map(r => ({ ...r, action: 'denied' })));
      }

      const approvedCount = results.filter(r => r.success && r.action === 'approved').length;
      const deniedCount = results.filter(r => r.success && r.action === 'denied').length;
      const errorCount = results.filter(r => !r.success).length;

      if (approvedCount > 0 || deniedCount > 0) {
        const messages = [];
        if (approvedCount > 0) {
          messages.push(`${approvedCount} aprovada${approvedCount > 1 ? 's' : ''}`);
        }
        if (deniedCount > 0) {
          messages.push(`${deniedCount} negada${deniedCount > 1 ? 's' : ''}`);
        }
        toast.success(`Solicitações processadas: ${messages.join(', ')}`);
      }
      
      if (errorCount > 0) {
        toast.error(`${errorCount} solicitação${errorCount > 1 ? 'ões' : ''} falharam`);
      }

      // Limpar seleções
      deselectAllForDoctor(doctorId);
      onRequestProcessed?.();
    } catch (error) {
      console.error('Erro no processamento das solicitações:', error);
      toast.error('Erro interno do servidor');
    } finally {
      setProcessingRequests(new Set());
    }
  };

  const getRequestMatrix = (requests: AccessRequest[]) => {
    // Criar matriz de solicitações por tipo de permissão e tipo de dados
    const matrix: Record<string, Record<string, AccessRequest | null>> = {};
    
    permissionTypes.forEach(perm => {
      matrix[perm.type] = {};
      dataTypes.forEach(data => {
        matrix[perm.type][data.type] = null;
      });
    });

    // Preencher matriz com solicitações existentes
    requests.forEach(request => {
      if (matrix[request.permission_type] && matrix[request.permission_type][request.data_type] !== undefined) {
        matrix[request.permission_type][request.data_type] = request;
      }
    });

    return matrix;
  };

  const hasEmergencyRequests = (requests: AccessRequest[]) => {
    return requests.some(req => 
      req.justification?.toLowerCase().includes('emergência') || 
      req.justification?.toLowerCase().includes('urgente')
    );
  };

  if (Object.keys(groupedRequests).length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Clock className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-center">
            Nenhuma solicitação pendente
          </p>
          <p className="text-sm text-gray-400 text-center mt-2">
            Quando médicos solicitarem acesso aos seus dados, elas aparecerão aqui
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {Object.entries(groupedRequests).map(([doctorId, { doctor, requests: doctorRequests }]) => {
        const isExpanded = expandedDoctors.has(doctorId);
        const selectedCount = Object.values(selectedPermissions[doctorId] || {}).filter(Boolean).length;
        const hasEmergency = hasEmergencyRequests(doctorRequests);
        const matrix = getRequestMatrix(doctorRequests);

        return (
          <Card 
            key={doctorId} 
            className={`${hasEmergency ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'} transition-all duration-200`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-800">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{doctor.name}</CardTitle>
                    <CardDescription>{doctor.email}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {hasEmergency && (
                    <Badge className="bg-red-100 text-red-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Emergência
                    </Badge>
                  )}
                  <Badge className="bg-blue-100 text-blue-800">
                    {doctorRequests.length} solicitação{doctorRequests.length !== 1 ? 'ões' : ''}
                  </Badge>
                  {selectedCount > 0 && (
                    <Badge className="bg-green-100 text-green-800">
                      {selectedCount} selecionada{selectedCount !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleDoctorExpansion(doctorId)}
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="space-y-6">
                {/* Matriz de Permissões - Visual idêntico ao PermissionGridSelector */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Matriz de Permissões</h4>
                  <p className="text-sm text-gray-600">
                    Selecione as permissões que deseja aprovar para este médico
                  </p>
                  
                  <div className="overflow-x-auto">
                    <div className="min-w-full">
                      {/* Cabeçalho com tipos de dados */}
                      <div className="grid grid-cols-7 gap-1 mb-3">
                        <div className="text-xs font-medium text-gray-500 p-1.5">
                          Permissões / Dados
                        </div>
                        {dataTypes.map((dataType) => (
                          <div key={dataType.type} className="text-center p-1">
                            <div className={`flex flex-col items-center space-y-0.5 ${dataType.color}`}>
                              {dataType.icon}
                              <span className="text-xs font-medium leading-tight">
                                {dataType.shortLabel}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Grid de permissões */}
                      <div className="space-y-2">
                        {permissionTypes.map((permission) => (
                          <div key={permission.type} className="grid grid-cols-7 gap-1">
                            {/* Cabeçalho da linha - tipo de permissão */}
                            <div className="flex items-center space-x-1.5 p-1.5">
                              <div className={`p-1 rounded-lg text-white ${permission.color}`}>
                                {permission.icon}
                              </div>
                              <span className="text-xs font-medium text-gray-700 leading-tight">
                                {permission.label}
                              </span>
                            </div>

                            {/* Células do grid */}
                            {dataTypes.map((dataType) => {
                              const request = matrix[permission.type][dataType.type];
                              const isSelected = request ? selectedPermissions[doctorId]?.[request.id] : false;
                              const isProcessing = request ? processingRequests.has(request.id) : false;
                              const isRequested = !!request;

                              return (
                                <div
                                  key={`${permission.type}-${dataType.type}`}
                                  className={`
                                    relative rounded-md border-2 transition-all duration-200 p-1.5
                                     ${
                                       isRequested
                                         ? isSelected
                                           ? `${permission.lightColor} border-green-300 text-green-800 cursor-pointer hover:shadow-sm hover:scale-102`
                                           : 'bg-red-50 border-red-200 text-red-600 cursor-pointer hover:shadow-sm hover:scale-102'
                                         : 'bg-gray-50 border-gray-100 opacity-30 cursor-not-allowed'
                                     }
                                     ${isProcessing ? 'opacity-50' : ''}
                                  `}
                                  onClick={() => {
                                     if (isRequested && !isProcessing) {
                                       // Permite clicar para alternar entre selecionado/desmarcado
                                       togglePermissionSelection(doctorId, request.id);
                                     }
                                   }}
                                >
                                  {isRequested ? (
                                     <div className="flex items-center justify-center h-6">
                                       {isSelected ? (
                                         <div className={`p-0.5 rounded-full bg-white`}>
                                           <CheckCircle className="h-3 w-3 text-green-600" />
                                         </div>
                                       ) : (
                                         <div className="flex items-center justify-center">
                                           <XCircle className="h-3 w-3 text-red-500" />
                                         </div>
                                       )}
                                     </div>
                                   ) : (
                                     <div className="h-6"></div>
                                   )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Controles de Seleção */}
                <div className="flex items-center gap-2 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectAllForDoctor(doctorId, doctorRequests)}
                  >
                    Selecionar Todas
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deselectAllForDoctor(doctorId)}
                  >
                    Limpar Seleção
                  </Button>
                  <span className="text-gray-500 ml-2">
                    {selectedCount} de {doctorRequests.length} selecionadas
                  </span>
                </div>

                {/* Configurações de Aprovação */}
                {(
                  <div className="space-y-4 p-4 bg-white rounded-lg border">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Duração do Acesso</label>
                      <DurationSelector
                        selectedDuration={customDuration}
                        onDurationChange={setCustomDuration}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Notas (opcional)</label>
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Adicione observações sobre esta aprovação..."
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        rows={2}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleProcessAllRequests(doctorId, doctorRequests)}
                        disabled={loading || processingRequests.size > 0}
                        className="bg-blue-600 hover:bg-blue-700 flex-1"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Processar Solicitações
                      </Button>
                    </div>
                    
                    {/* Feedback visual sobre o que será processado */}
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span>📋 <strong>Resumo do processamento:</strong></span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {selectedCount > 0 && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-700">
                              {selectedCount} solicitação{selectedCount !== 1 ? 'ões' : ''} será{selectedCount !== 1 ? 'ão' : ''} <strong>aprovada{selectedCount !== 1 ? 's' : ''}</strong>
                            </span>
                          </div>
                        )}
                        {(doctorRequests.length - selectedCount) > 0 && (
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-red-700">
                              {doctorRequests.length - selectedCount} solicitação{(doctorRequests.length - selectedCount) !== 1 ? 'ões' : ''} será{(doctorRequests.length - selectedCount) !== 1 ? 'ão' : ''} <strong>negada{(doctorRequests.length - selectedCount) !== 1 ? 's' : ''}</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista de Solicitações Detalhadas */}
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-700">Detalhes das Solicitações</h5>
                  {doctorRequests.map((request) => (
                    <div key={request.id} className="p-3 bg-white rounded border text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={`${permissionTypes.find(p => p.type === request.permission_type)?.lightColor || 'bg-gray-100'} text-gray-800`}>
                            {permissionTypes.find(p => p.type === request.permission_type)?.label || request.permission_type}
                          </Badge>
                          <Badge className={`bg-gray-100 text-gray-800`}>
                            {dataTypes.find(d => d.type === request.data_type)?.label || request.data_type}
                          </Badge>
                        </div>
                        <span className="text-gray-500">
                          {formatDistanceToNow(new Date(request.created_at), { addSuffix: true, locale: ptBR })}
                        </span>
                      </div>
                      {request.justification && (
                        <p className="mt-2 text-gray-600">
                          <strong>Justificativa:</strong> {request.justification}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default DoctorGroupedApprovalMatrix;